/**
 * @license
 * @preserve
 *
 * KeeeX SAS Confidential
 * https://keeex.me
 * Copyright 2013-2022 KeeeX All Rights Reserved.
 * 
 * These computer program listings and specifications, herein,
 * are and remain the property of KeeeX SAS. The intellectual
 * and technical concepts herein are proprietary to KeeeX SAS
 * and may be covered by EU and foreign patents,
 * patents in process, trade secrets and copyright law.
 * 
 * They shall not be reproduced or copied or used in whole or
 * in part as the basis for manufacture or sale of items unless
 * prior written permission is obtained from KeeeX SAS.
 * 
 * For a license agreement, please contact:
 * <mailto: contact@keeex.net>
 * 
 */
import { Algorithms as CipherAlgorithms } from "@keeex/crypto/lib/cipher.js";
import { notImplemented } from "@keeex/crypto/lib/exception.js";
import { getUint8Array } from "@keeex/js-utils/lib/uint8array.js";
import { getNameFromAlgorithm } from "./util.js";
const aesCtrParam = iv => ({
  name: getNameFromAlgorithm(CipherAlgorithms.AESCTR),
  counter: iv,
  length: 64
});
const aesCbcParam = iv => ({
  name: getNameFromAlgorithm(CipherAlgorithms.AESCBC),
  iv
});
const encryptRawImmediateAESCTR = async (data, iv, key) => {
  const ciphertext = await crypto.subtle.encrypt(aesCtrParam(iv), key.forCipher(), data);
  return getUint8Array(ciphertext);
};
const encryptRawImmediateAESCBC = async (data, iv, key) => {
  const ciphertext = await crypto.subtle.encrypt(aesCbcParam(iv), key.forCipher(), data);
  return getUint8Array(ciphertext);
};
const encryptRawImmediate = (data, iv, key) => {
  switch (key.algorithm()) {
    case CipherAlgorithms.AESCTR:
      return encryptRawImmediateAESCTR(data, iv, key);
    case CipherAlgorithms.AESCBC:
      return encryptRawImmediateAESCBC(data, iv, key);
    default:
      throw notImplemented();
  }
};
const decryptRawImmediateAESCTR = async (data, iv, key) => {
  const plain = await crypto.subtle.decrypt(aesCtrParam(iv), key.forCipher(), data);
  return getUint8Array(plain);
};
const decryptRawImmediateAESCBC = async (data, iv, key) => {
  const plain = await crypto.subtle.decrypt(aesCbcParam(iv), key.forCipher(), data);
  return getUint8Array(plain);
};
const decryptRawImmediate = (data, iv, key) => {
  switch (key.algorithm()) {
    case CipherAlgorithms.AESCTR:
      return decryptRawImmediateAESCTR(data, iv, key);
    case CipherAlgorithms.AESCBC:
      return decryptRawImmediateAESCBC(data, iv, key);
    default:
      throw notImplemented();
  }
};
const createEncryptionContext = async (key, iv) => {
  // Browser available crypto.subtle does not have interface for block-based encryption
  const pureKey = await key.toPureJsKey();
  const {
    default: pureCipherProvider
  } = await import("@keeex/crypto-provider-purejs/lib/cipher/index.js");
  if (!pureCipherProvider.createEncryptionContext) {
    throw new Error("Unexpected state");
  }
  return pureCipherProvider.createEncryptionContext(pureKey, iv);
};
const createDecryptionContext = async (key, iv) => {
  // Browser available crypto.subtle does not have interface for block-based decryption
  const pureKey = await key.toPureJsKey();
  const {
    default: pureCipherProvider
  } = await import("@keeex/crypto-provider-purejs/lib/cipher/index.js");
  if (!pureCipherProvider.createDecryptionContext) {
    throw new Error("Unexpected state");
  }
  return pureCipherProvider.createDecryptionContext(pureKey, iv);
};
const cipherProvider = {
  encryptRawImmediate,
  decryptRawImmediate,
  createEncryptionContext,
  createDecryptionContext
};
export default cipherProvider;
